import * as React from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import axios from "axios";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import api, { API_BASE } from "../lib/api";
import {
  clearTokens,
  getDecodedAccess,
  getRefreshToken,
} from "../lib/auth";
import { SUPPLY_CHAIN_DASHBOARD_URL } from "../config/urls";
import { canUseRecommender } from "../lib/recommenderAccess";

const NAV_ITEMS = [
  { label: "Home", path: "/", icon: HomeRoundedIcon },
  { label: "INNovakis Blog", path: "/blog", icon: ArticleOutlinedIcon },
  {
    label: "Recommender",
    path: "/plant",
    icon: AnalyticsOutlinedIcon,
    requiresRecommender: true,
  },
  {
    label: "Supply Chain",
    path: SUPPLY_CHAIN_DASHBOARD_URL,
    external: true,
    icon: LocalShippingOutlinedIcon,
  },
  { label: "About", path: "/about", icon: InfoOutlinedIcon },
];

function initials(firstName, lastName, username) {
  const value = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.trim();
  return (value || username?.[0] || "U").toUpperCase();
}

function formatNotificationTime(value) {
  if (!value) return "";
  const diffSeconds = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  const units = [
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const match = units.find(([, seconds]) => diffSeconds >= seconds);
  if (!match) return "just now";
  const [unit, seconds] = match;
  const count = Math.floor(diffSeconds / seconds);
  return `${count} ${unit}${count > 1 ? "s" : ""} ago`;
}

export default function TopNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const decoded = getDecodedAccess();
  const userId = decoded?.user_id;
  const [profile, setProfile] = React.useState({
    first_name: "",
    last_name: "",
    username: decoded?.username || "User",
    email: decoded?.email || "",
    avatar: "",
  });
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [notificationAnchor, setNotificationAnchor] = React.useState(null);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.requiresRecommender || canUseRecommender()
  );

  React.useEffect(() => {
    if (!userId) return;

    let active = true;
    api
      .get(`/${userId}/getuserprofile`)
      .then(({ data }) => {
        if (active) setProfile((current) => ({ ...current, ...data }));
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [userId]);

  const fetchNotifications = React.useCallback(() => {
    if (!userId) return;
    api
      .get("/notifications")
      .then(({ data }) => {
        setNotifications(data?.results || []);
        setUnreadCount(data?.unread_count || 0);
      })
      .catch(() => {});
  }, [userId]);

  React.useEffect(() => {
    fetchNotifications();
    const timer = window.setInterval(fetchNotifications, 60000);
    return () => window.clearInterval(timer);
  }, [fetchNotifications]);

  React.useEffect(() => {
    const handleProfileUpdated = (event) => {
      setProfile((current) => ({ ...current, ...(event.detail || {}) }));
    };
    window.addEventListener("seeds-profile-updated", handleProfileUpdated);
    return () => {
      window.removeEventListener("seeds-profile-updated", handleProfileUpdated);
    };
  }, []);

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const fullName =
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    profile.username ||
    profile.email ||
    "User";

  const closeMenus = () => {
    setMenuAnchor(null);
    setNotificationAnchor(null);
    setMobileOpen(false);
  };

  const notificationText = (notification) => {
    const actor = notification.actor_name || "User";
    if (notification.notification_type === "comment_reply") {
      return `${actor} replied to your comment`;
    }
    if (notification.notification_type === "comment_like") {
      return `${actor} liked your comment`;
    }
    return `${actor} commented on your post`;
  };

  const handleNotificationClick = async (notification) => {
    try {
      await api.post(`/notifications/${notification.id}/read`);
    } catch {
      // Navigation should still happen if marking read fails.
    }
    setUnreadCount((count) => Math.max(0, count - (notification.is_read ? 0 : 1)));
    setNotifications((items) => items.filter((item) => item.id !== notification.id));
    setNotificationAnchor(null);
    navigate(
      notification.target_url ||
        `/blog/${notification.post_id || notification.post_slug}`,
    );
  };

  const handleLogout = async () => {
    closeMenus();
    try {
      const refresh = getRefreshToken();
      if (refresh) {
        await axios.post(`${API_BASE}/blacklist_token`, { refresh });
      }
    } catch {
      // Local logout must still complete if the token is already invalid.
    } finally {
      clearTokens();
      navigate("/signin");
    }
  };

  const userActions = (
    <>
      <MenuItem
        component={RouterLink}
        to={`/u/${encodeURIComponent(profile.username || "")}`}
        onClick={closeMenus}
      >
        <ListItemIcon>
          <PersonOutlineRoundedIcon fontSize="small" />
        </ListItemIcon>
        My Profile
      </MenuItem>
      {/* <MenuItem component={RouterLink} to="/profile" onClick={closeMenus}>
        <ListItemIcon>
          <SettingsOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Account Settings
      </MenuItem> */}
      <Divider />
      <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
        <ListItemIcon sx={{ color: "inherit" }}>
          <LogoutRoundedIcon fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </>
  );

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "rgba(255,255,255,0.96)",
          borderBottom: "1px solid",
          borderColor: "divider",
          backdropFilter: "blur(10px)",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: "70px !important" }}>
            <Stack
              component={RouterLink}
              to="/"
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ color: "text.primary", textDecoration: "none", flexShrink: 0 }}
            >
              <Box
                component="img"
                src="/images/seedsLogo.png"
                alt="SEEDS"
                sx={{ width: 60, height: 60, objectFit: "contain" }}
              />
              <Typography
                sx={{ fontSize: "1rem", fontWeight: 700 }}
              >
                SEEDS Platform
              </Typography>
            </Stack>

            <Stack
              component="nav"
              direction="row"
              spacing={0.6}
              aria-label="Main navigation"
              sx={{
                display: { xs: "none", md: "flex" },
                flex: 1,
                justifyContent: "center",
                px: 2,
              }}
            >
              {visibleNavItems.map(({ label, path, external, icon: Icon }) => (
                <Button
                  key={path}
                  component={external ? "a" : RouterLink}
                  href={external ? path : undefined}
                  to={external ? undefined : path}
                  size="small"
                  startIcon={<Icon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    minWidth: 0,
                    minHeight: 36,
                    px: 1.5,
                    color:
                      !external && isActive(path)
                        ? "#3e3100"
                        : "text.secondary",
                    bgcolor:
                      !external && isActive(path) ? "#f7b900" : "transparent",
                    fontSize: "0.82rem",
                    fontWeight: !external && isActive(path) ? 700 : 500,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor:
                        !external && isActive(path)
                          ? "#e5ac00"
                          : "action.hover",
                    },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
              <IconButton
                aria-label="Open notifications"
                onClick={(event) => setNotificationAnchor(event.currentTarget)}
                sx={{ color: "text.secondary" }}
              >
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#f7b900",
                      color: "#312600",
                      fontWeight: 800,
                    },
                  }}
                >
                  <NotificationsNoneRoundedIcon />
                </Badge>
              </IconButton>

              <Button
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                aria-label="Open user menu"
                aria-controls={menuAnchor ? "user-menu" : undefined}
                aria-expanded={menuAnchor ? "true" : undefined}
                sx={{
                  minWidth: 0,
                  p: 0.4,
                  borderRadius: 99,
                  color: "text.primary",
                }}
              >
                <Avatar
                  src={profile.avatar || undefined}
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: "#f7b900",
                    color: "#453600",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {initials(
                    profile.first_name,
                    profile.last_name,
                    profile.username,
                  )}
                </Avatar>
                <KeyboardArrowDownRoundedIcon sx={{ ml: 0.4, fontSize: 19 }} />
              </Button>
            </Stack>

            <Stack
              direction="row"
              spacing={0.25}
              alignItems="center"
              sx={{ display: { xs: "flex", md: "none" }, ml: "auto" }}
            >
              <IconButton
                aria-label="Open notifications"
                onClick={(event) => setNotificationAnchor(event.currentTarget)}
                sx={{ color: "text.secondary" }}
              >
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#f7b900",
                      color: "#312600",
                      fontWeight: 800,
                    },
                  }}
                >
                  <NotificationsNoneRoundedIcon />
                </Badge>
              </IconButton>

              <IconButton
                aria-label="Open navigation menu"
                onClick={() => setMobileOpen(true)}
              >
                <MenuRoundedIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Menu
        id="notifications-menu"
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={() => setNotificationAnchor(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{
          paper: {
            sx: { mt: 1, width: 340, maxWidth: "calc(100vw - 24px)", border: "1px solid", borderColor: "divider" },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontWeight: 800 }}>Notifications</Typography>
        </Box>
        <Divider />
        {notifications.length ? (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                alignItems: "flex-start",
                gap: 1.25,
                whiteSpace: "normal",
                bgcolor: notification.is_read ? "#fff" : "#fffdf4",
                py: 1.25,
              }}
            >
              <Avatar
                src={notification.actor_avatar || undefined}
                sx={{ width: 34, height: 34, bgcolor: "#f7b900", color: "#312600", fontSize: 12, fontWeight: 800 }}
              >
                {initials("", "", notification.actor_name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: notification.is_read ? 600 : 800 }}>
                  {notificationText(notification)}
                </Typography>
                {notification.comment_excerpt ? (
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }} noWrap>
                    "{notification.comment_excerpt}"
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                  {formatNotificationTime(notification.created_at)}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
              No notifications yet.
            </Typography>
          </Box>
        )}
      </Menu>

      <Menu
        id="user-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{
          paper: {
            sx: { mt: 1, minWidth: 220, border: "1px solid", borderColor: "divider" },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {profile.email}
          </Typography>
        </Box>
        <Divider />
        {userActions}
      </Menu>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        slotProps={{
          paper: { sx: { width: "min(86vw, 320px)", pt: "70px" } },
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25 }}>
          <Avatar
            src={profile.avatar || undefined}
            sx={{ bgcolor: "#f7b900", color: "#453600", mr: 1.25 }}
          >
            {initials(
              profile.first_name,
              profile.last_name,
              profile.username,
            )}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {profile.email}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close navigation menu"
            onClick={() => setMobileOpen(false)}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Divider />
        <List component="nav" sx={{ px: 1, py: 1.5 }}>
          {visibleNavItems.map(({ label, path, external, icon: Icon }) => (
            <ListItemButton
              key={path}
              component={external ? "a" : RouterLink}
              href={external ? path : undefined}
              to={external ? undefined : path}
              selected={!external && isActive(path)}
              onClick={closeMenus}
              sx={{
                mb: 0.5,
                borderRadius: 1.5,
                "&.Mui-selected": { bgcolor: "#f7b900" },
                "&.Mui-selected:hover": { bgcolor: "#e5ac00" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ mt: "auto" }}>
          <Divider />
          <List sx={{ px: 1, py: 1 }}>{userActions}</List>
        </Box>
      </Drawer>
    </>
  );
}
